import React from 'react';
import { useCommunityInitialization } from 'hooks/useCommunityInitialization';
import { getAuthUrl, getForumsSignInUrl, getForumsSignOutUrl } from '../../utils/url-generator';
import { useContext, useState, useRef, useEffect } from 'react';
import { UserContext } from '../../context/UserContext';
import { ConsentValidator, initializeCommunity } from '@atlassian/community-library';
import WelcomeIllustration from '../../assets/images/welcome-image.png';
import ExternalRedirect from '../common/ExternalRedirect';
import LoadingSpinner from '@atlassian/community-library/dist/components/loading-spinner';
import { sendNewAccountEvent } from '../../utils/send-newaccount-event';
import ErrorPage from '../error/Error';

type InitValidatorProps = {
  children: React.ReactNode;
};

const typedGlobalThis = globalThis as typeof globalThis & {
  __IS_CONSENTED__: boolean;
  __ACCOUNTS_INITIALIZED__: boolean;
  isSSR: boolean;
  __IS_NEW_FORUMS_PROFILE__: boolean;
};

export const InitValidator = ({ children }: InitValidatorProps) => {
  const { isChecking, userHasConsented, userIsInitialized, initializationError } = useCommunityInitialization();
  const userData = useContext(UserContext);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSignupAnalyticsSent, setIsSignupAnalyticsSent] = useState<boolean>(userIsInitialized);
  const isLocal = process.env['REACT_APP_ENVI'] === 'local';
  const homepage = isLocal ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  // Check for SSR state to avoid unnecessary consent checks
  const isSSR = typedGlobalThis.isSSR;
  const ssrUserConsented = typedGlobalThis.__IS_CONSENTED__;
  const ssrUserInitialized = typedGlobalThis.__ACCOUNTS_INITIALIZED__;

  // Persist redirectParam across renders
  const searchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const initialRedirectParam = searchParams.get('redirect');
  const redirectParamRef = useRef(initialRedirectParam);

  const initializeAndRedirect = async () => {
    try {
      if (!userData?.user?.account_id && !userData?.user?.email) {
        throw new Error('User account ID is required');
      }
      console.log('Initializing community for user:', userData?.user?.account_id);
      const response = await initializeCommunity(userData?.user?.email, homepage, homepage);

      if (!response) {
        console.error('[useCommunityInitialization] Community initialization failed - no response');
        throw new Error('could not initialize community');
      } else {
        await sendNewAccountEvent(response?.isNewForumsProfile, userData?.user?.account_id);
        setIsSignupAnalyticsSent(true);
      }

      handleRedirect();
    } catch (error) {
      console.error('[useCommunityInitialization] Error initializing community:', error);
      throw new Error('could not initialize community');
      // redirect to error page.
    }
  };

  // Handle redirect to forums or homepage
  const handleRedirect = (shouldLogout = false) => {
    if (typeof window === 'undefined') return;

    const redirectParam = redirectParamRef.current;
    const currentPath = window.location.pathname || '/';
    const fullRedirectUrl = `${process.env['HOME_BASE_URL']}${redirectParam ? redirectParam : '/'}`;

    const handleForumsLogout = () => {
      const forumsLogoutUrl = getForumsSignOutUrl(`${process.env['KHOROS_ENDPOINT_PATH']}`);
      const forumsLogoutAbsoluteUrl = `${process.env['HOME_BASE_URL']}/${forumsLogoutUrl}`;
      const identityLogoutUrl = getAuthUrl('logout', {
        redirectUrl: forumsLogoutAbsoluteUrl,
        experience: 'entry-auth',
        email: userData?.user?.email,
      });
      setRedirectUrl(identityLogoutUrl);
    };

    const handleNonForumsLogout = () => {
      const logoutPath = getAuthUrl('logout', {
        redirectUrl: `${process.env['HOME_BASE_URL']}/`,
        experience: 'entry-auth',
        email: userData?.user?.email,
      });
      setRedirectUrl(logoutPath);
    };

    const handleForumsConsent = () => {
      const forumsSignInUrl = getForumsSignInUrl({
        redirectUrl: fullRedirectUrl,
        email: userData?.user?.email,
      });

      setRedirectUrl(`${process.env['HOME_BASE_URL']}${forumsSignInUrl}`);
    };

    const handleNonForumsConsent = () => {
      setRedirectUrl(`${process.env['HOME_BASE_URL']}/`);
    };

    const handleDefaultLogout = () => {
      const logoutPath = getAuthUrl('logout', {
        redirectUrl: `${process.env['HOME_BASE_URL']}${currentPath}`,
        experience: 'entry-auth',
        email: userData?.user?.email,
      });
      setRedirectUrl(logoutPath);
    };

    const handleDefaultConsent = () => {
      setRedirectUrl(`${process.env['HOME_BASE_URL']}${currentPath}`);
    };

    if (redirectParam) {
      if (shouldLogout) {
        if (redirectParam.includes('/forums')) {
          handleForumsLogout();
        } else {
          handleNonForumsLogout();
        }
      } else {
        if (redirectParam.includes('/forums')) {
          handleForumsConsent();
        } else {
          handleNonForumsConsent();
        }
      }
    } else {
      if (shouldLogout) {
        handleDefaultLogout();
      } else {
        handleDefaultConsent();
      }
    }
  };

  useEffect(() => {
    if (userData !== undefined) {
      (async () => {
        if (ssrUserInitialized) {
          try {
            await sendNewAccountEvent(typedGlobalThis.__IS_NEW_FORUMS_PROFILE__ ?? false, userData?.user?.account_id);
          } catch (error) {
            console.error('[InitValidator] Error sending new account event:', error);
          }
        }
        setIsSignupAnalyticsSent(true);
        setIsLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  // If we have a redirect URL, use ExternalRedirect
  if (redirectUrl) {
    return <ExternalRedirect to={redirectUrl} />;
  }

  // For SSR users who are already consented and initialized, skip the consent check
  // This prevents the blinking spinner/modal issue
  if (isSSR && ssrUserConsented && ssrUserInitialized) {
    // If user has consented and there's a redirect param, handle redirect
    if (redirectParamRef.current) {
      handleRedirect();
      return <LoadingSpinner />;
    }
    // Otherwise, just show children
    return <>{children}</>;
  }

  // While checking consent status, show a loading indicator
  if (isChecking || !isSignupAnalyticsSent) {
    return <LoadingSpinner />;
  }

  // If there was an initialization error, show error page
  if (initializationError) {
    return <ErrorPage />;
  }

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!userData?.user) {
    // Not logged in, just show children (homepage)
    return <>{children}</>;
  }

  // If user has consented and there's a redirect param, handle redirect
  if (userHasConsented && userIsInitialized && redirectParamRef.current) {
    handleRedirect();
    return <LoadingSpinner />;
  }

  // Check consent first, before checking initialization
  if (!userHasConsented) {
    return (
      <ConsentValidator
        onLogout={() => {
          handleRedirect(true);
        }}
        onConsentError={() => {
          handleRedirect();
        }}
        onConsentSuccess={() => {
          initializeAndRedirect();
        }}
        aaid={userData?.user?.account_id || 'test-user-id'}
        isUserConsented={false}
        coverImageSrc={WelcomeIllustration}
      >
        <AfterConsentRedirect onConsentGiven={() => {}} />
      </ConsentValidator>
    );
  }

  // Only check initialization after we know user has consented
  if (userIsInitialized) {
    // If we're already on the target page (no redirect param), just show children
    if (!redirectParamRef.current) {
      return <>{children}</>;
    }
    handleRedirect();
    return <LoadingSpinner />;
  }

  // Otherwise, we're initializing - show loading spinner
  return <LoadingSpinner />;
};

// Add AfterConsentRedirect component
const AfterConsentRedirect = ({ onConsentGiven }: { onConsentGiven: () => void }) => {
  // This effect will run as soon as the modal closes (i.e., after consent is accepted)
  useEffect(() => {
    onConsentGiven();
  }, [onConsentGiven]);
  return null;
};
